import { useRef, useState } from "react";
import axios from "axios";
import { Check, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABEL = "font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground";

export function LeadForm({ industry, accent = "#0FAE96", source = "solutions" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [hp, setHp] = useState(""); // honeypot — must stay empty
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const mountedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    setError("");
    setPending(true);
    try {
      const { data } = await axios.post(`${API}/lead/industry`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        industry,
        source,
        origin: window.location.origin,
        company_website: hp, // honeypot
        elapsed_ms: Date.now() - mountedAt.current,
      });
      if (data.status === "pending_confirmation") setDone(true);
      else setError("Unable to submit at this time. Please try again later.");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to submit at this time. Please try again later.");
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success-message"
        className="rounded-2xl border border-border/60 bg-card/60 p-8 backdrop-blur"
      >
        <div
          className="inline-flex h-11 w-11 items-center justify-center rounded-full"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          <MailCheck className="h-5 w-5" strokeWidth={2.2} />
        </div>
        <h3 className="mt-4 text-xl font-semibold tracking-tight">Check your email.</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          We sent a confirmation link to <span className="font-mono text-foreground">{form.email}</span>.
          Click it to confirm your request — our team will reach out within one business day once you do.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-form"
      onSubmit={submit}
      className="rounded-2xl border border-border/60 bg-card/60 p-7 backdrop-blur sm:p-8"
    >
      <h3 className="text-xl font-semibold tracking-tight">Book a walkthrough</h3>
      <p className="mt-1.5 text-sm text-muted-foreground">
        See Sema answer your questions against {industry ? `${industry} ` : ""}data. We reply within one business day.
      </p>

      {/* Honeypot: hidden from humans, attractive to bots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden">
        <label>
          Company website
          <input
            tabIndex={-1}
            autoComplete="off"
            data-testid="lead-form-hp"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
          />
        </label>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-1">
          <Label htmlFor="lf-name" className={FIELD_LABEL}>Name</Label>
          <Input id="lf-name" data-testid="lead-form-name" required minLength={2}
            value={form.name} onChange={set("name")} placeholder="Avery Lin" className="mt-2" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="lf-company" className={FIELD_LABEL}>Company</Label>
          <Input id="lf-company" data-testid="lead-form-company" required minLength={2}
            value={form.company} onChange={set("company")} placeholder="Northwind Health" className="mt-2" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-email" className={FIELD_LABEL}>Work email</Label>
          <Input id="lf-email" type="email" data-testid="lead-form-email" required
            value={form.email} onChange={set("email")} placeholder="avery@northwind.com" className="mt-2" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-message" className={FIELD_LABEL}>What do you want to answer?</Label>
          <Textarea id="lf-message" data-testid="lead-form-message" required minLength={10} rows={3}
            value={form.message} onChange={set("message")}
            placeholder="E.g. Which facilities are above target on 30-day readmissions?" className="mt-2" />
        </div>
      </div>

      {error && <p data-testid="lead-form-error" className="mt-4 text-sm text-destructive">{error}</p>}

      <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Double opt-in · no spam
        </p>
        <Button
          type="submit"
          data-testid="lead-form-submit"
          size="lg"
          disabled={pending}
          className="rounded-full px-6 font-mono text-xs uppercase tracking-[0.2em] text-white hover:opacity-90"
          style={{ backgroundColor: accent }}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request walkthrough <Check className="ml-2 h-4 w-4" /></>}
        </Button>
      </div>
    </form>
  );
}
